import React, { memo, useState, useCallback } from "react";
import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ContentCopy,
  MoreVert,
  OpenInNew,
  LocalShipping,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { filter, join } from "lodash";
import copy from "copy-to-clipboard";

const $COLUMN_WIDTH = 200;

/**
 * Renders warehouse information with tooltip
 */
export const WarehouseCell = memo(function WarehouseCell({ value }) {
  const { city = "", area = "", street = "" } = value?.address || {};
  const addressComponents = filter([city, area, street], (comp) => !!comp);
  const address = join(addressComponents, " - ");

  return (
    <Tooltip
      placement="top"
      title={
        <div>
          <Typography fontSize={16}>{value?.name}</Typography>
          <Typography fontSize={14}>{address}</Typography>
        </div>
      }
    >
      <div>
        <Typography
          fontSize={16}
          noWrap
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            maxWidth: $COLUMN_WIDTH * 0.9,
          }}
        >
          {value?.name}
        </Typography>
        <Typography
          fontSize={14}
          color="textSecondary"
          noWrap
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            maxWidth: $COLUMN_WIDTH * 0.9,
          }}
        >
          {address}
        </Typography>
      </div>
    </Tooltip>
  );
});

/**
 * Renders customer name with tooltip
 */
export const CustomerNameCell = memo(function CustomerNameCell({ row }) {
  const name = row?.user?.name || "";

  return (
    <Tooltip title={name} placement="top">
      <Typography
        variant="body2"
        noWrap
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
          maxWidth: $COLUMN_WIDTH * 0.8,
        }}
      >
        {name}
      </Typography>
    </Tooltip>
  );
});

/**
 * Renders customer address with tooltip
 */
export const CustomerAddressCell = memo(function CustomerAddressCell({ row }) {
  if (!row?.user?.address) {
    return <span>-</span>;
  }

  const { city, area, street } = row.user.address;
  const addressComponents = filter([area, street], (comp) => !!comp);
  const address = join(addressComponents, " - ");

  return (
    <Tooltip
      placement="top"
      title={
        <div>
          <Typography fontSize={16}>{city}</Typography>
          <Typography fontSize={14}>{address}</Typography>
        </div>
      }
    >
      <div>
        <Typography
          fontSize={16}
          noWrap
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            maxWidth: $COLUMN_WIDTH * 0.9,
          }}
        >
          {city}
        </Typography>
        <Typography
          fontSize={14}
          color="textSecondary"
          noWrap
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            maxWidth: $COLUMN_WIDTH * 0.9,
          }}
        >
          {address}
        </Typography>
      </div>
    </Tooltip>
  );
});

/**
 * Renders shipping company info with avatar
 */
export const ShippingCompanyCell = memo(function ShippingCompanyCell({
  value,
}) {
  const shipment = value?.returnShipment ?? value?.shipment;
 const theme = useTheme();  

  if (!shipment?.deliveryOption?.deliveryOptionName) {
    return null;
  }

  return (
    <Tooltip title={shipment.deliveryOption.deliveryOptionName} placement="top">
      <Box
        display="flex"
        alignItems="center"
        maxWidth={$COLUMN_WIDTH * 0.8}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <Avatar variant="rounded" src={shipment.deliveryOption.logo} />
        <Typography
    variant="subtitle1"
    sx={{
      ml: 1.5,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: 1,         
      minWidth: 0,
      direction:theme.direction === "rtl" ? "rtl" : "ltr",
    }}
  >
    {shipment.deliveryOption.deliveryOptionName}
  </Typography>
      </Box>
    </Tooltip>
  );
});

/**
 * Renders shipment tracking number with action menu
 */
export const ShipmentTrackingCell = memo(function ShipmentTrackingCell({
  row,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const shipment =
    row?.otoIntegration?.returnShipment ?? row?.otoIntegration?.shipment;
  const shipmentTrackingNumber = shipment?.trackingNumber || "";

  const handleOpenMenu = useCallback((event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const copyTrackingNumber = useCallback(
    (event) => {
      event.stopPropagation();
      if (shipmentTrackingNumber) {
        copy(shipmentTrackingNumber);
        handleCloseMenu();
      }
    },
    [shipmentTrackingNumber, handleCloseMenu],
  );

  const openTrackingUrl = useCallback(
    (event) => {
      event.stopPropagation();
      handleCloseMenu();
      if (shipment?.trackingUrl) {
        window.open(shipment.trackingUrl, "_blank", "noreferrer");
      }
    },
    [shipment?.trackingUrl, handleCloseMenu],
  );

  const handlePopoverClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  if (!shipmentTrackingNumber) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap={1} width={"100%"}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: $COLUMN_WIDTH * 0.7,
        }}
      >
        {shipmentTrackingNumber}
      </Box>

      <IconButton size="small" onClick={handleOpenMenu}>
        <MoreVert />
      </IconButton>
      <Popover
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        onClick={handlePopoverClick}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: theme.shadows[2],
            minWidth: 100,
            borderRadius: 1,
          },
        }}
      >
        <List dense sx={{ py: 0 }}>
          <ListItem
            onClick={copyTrackingNumber}
            sx={{
              cursor: "pointer",
              py: 1,
              px: 2,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("Copy")}
              primaryTypographyProps={{ fontSize: 14 }}
            />
          </ListItem>
          <ListItem
            onClick={openTrackingUrl}
            sx={{
              cursor: "pointer",
              py: 1,
              px: 2,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <OpenInNew fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("Traking Shipment")}
              primaryTypographyProps={{ fontSize: 14 }}
            />
          </ListItem>
        </List>
      </Popover>
    </Box>
  );
});

/**
 * Renders return shipment button
 */
export const ReturnShipmentCell = memo(function ReturnShipmentCell({
  row,
  onCreateReturn,
}) {
  const { t } = useTranslation();

  const handleClick = useCallback(
    (event) => {
      event.stopPropagation();
      onCreateReturn(row);
    },
    [row, onCreateReturn],
  );

  if (row?.otoIntegration?.returnShipment?.currentStatus === "pending") {
    return (
      <Typography variant="subtitle2">
        {t("Return shipment created")}
      </Typography>
    );
  }

  return (
    <IconButton onClick={handleClick} color="primary">
      <LocalShipping />
    </IconButton>
  );
});
